/**
 * 法令チェック — what the review found, and what somebody decided about it.
 *
 * The panel's job is to make a finding ACTIONABLE, which means four things the
 * previous版 did not have:
 *
 *   the evidence. Every corpus-backed finding names its provision - publisher,
 *   heading path, the quoted text - because a finding without its citation is an
 *   opinion, and a clinic cannot act on an opinion about the law.
 *
 *   where it is. The offset is carried through from the server, so 該当箇所へ移動
 *   selects the sentence in the editor rather than leaving somebody to search for
 *   it.
 *
 *   who said so. A deterministic pattern hit and a corpus-backed reading fail in
 *   different ways and deserve different scepticism, so each says which it is.
 *
 *   what was decided. 対応した / 問題なし / エスカレーション, with the reason
 *   recorded - and attached to the finding rather than to the run, so it survives
 *   the next check instead of being asked again every week.
 *
 * The verdict vocabulary is 指摘なし / 要確認 / 根拠不足 and never 適合: these
 * checks can show that something looks wrong, never that an article is lawful.
 */
import { useState } from 'react';
import { ApiError, post, put, type ArticleReview, type ReviewFinding } from '../api.ts';
import { Card, Empty, Pill, jaDate } from './ui.tsx';

/**
 * What a finding's stored state is called on screen.
 *
 * THREE OF THESE ARE HISTORY. 対応した / 問題なし / エスカレーション were the only
 * things a finding offered, and all three did the same thing: write a row and
 * dim the card. Nothing gated on them - the verdict counted a dismissed finding
 * exactly like an open one, publishing was never blocked by either, and an
 * escalation notified nobody. Three labels for one mechanism, none of which
 * moved the article an inch closer to being publishable.
 *
 * What a writer actually needs at a finding is: show me where it is, propose a
 * fix, or leave it alone. The first two are work; the third is the only one that
 * needs remembering, and 無視 is what it honestly is.
 *
 * The old states stay readable because old rows still carry them - a finding
 * decided last month must not render as a blank.
 */
const DISPOSITION_LABEL: Record<string, string> = {
  DISMISSED: '無視',
  ACCEPTED: '対応した',
  ESCALATED: 'エスカレーション',
};

/** One finding: the claim, the evidence, and what to do about it. */
function Finding({
  finding,
  articleId,
  onJump,
  onFix,
  busy: fixing,
  onChanged,
}: {
  finding: ReviewFinding;
  articleId: string;
  onJump: (at: number, quote: string) => void;
  /**
   * Propose a replacement for the sentence, from this finding's own reasoning.
   *
   * The panel already shows what is wrong, what to do instead and the provision
   * it comes from - which is exactly what a rewrite needs to be told, and
   * exactly what a clinic's marketing assistant is otherwise left to interpret
   * alone. Nothing is applied by this: the proposal lands in the editor's own
   * rewrite panel, beside the original, for the writer to accept or close.
   */
  onFix: (input: {
    quote: string;
    title: string;
    guidance?: string;
    citation?: { publisher: string; title: string; excerpt: string } | null;
  }) => void;
  busy: boolean;
  onChanged: () => Promise<void>;
}) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  /**
   * Remember that this finding was left alone, or stop remembering.
   *
   * The only state worth storing. It is keyed by a fingerprint of the rule plus
   * the sentence, so 無視 survives re-running the check - which is the whole
   * point: without it every re-run asks the same settled question again, and a
   * panel that re-asks is a panel people stop reading.
   *
   * No reason is demanded. It was, when this button claimed 「問題なし」 - a
   * judgement somebody defends later deserves a sentence explaining it. 無視 is
   * a smaller claim: not "this is lawful" but "I have seen it and I am moving
   * on", and demanding an essay for that is how a review panel becomes something
   * people work around.
   */
  async function decide(state: 'OPEN' | 'DISMISSED') {
    setBusy(true);
    setError(null);
    try {
      await put(`/articles/${articleId}/review/findings/${finding.fingerprint}`, { state });
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : '記録できませんでした');
    } finally {
      setBusy(false);
    }
  }

  const decided = finding.disposition && finding.disposition.state !== 'OPEN';

  return (
    <div className={`finding${decided ? ' finding--decided' : ''}`}>
      <p style={{ margin: '0 0 4px' }}>
        <Pill tone={finding.severity === '要確認' ? 'warn' : 'muted'}>{finding.severity}</Pill>{' '}
        <strong>{finding.title}</strong>{' '}
        {/* Which half found it: a pattern hit and a corpus reading fail
            differently and deserve different scepticism. */}
        <Pill tone="muted">{finding.source === 'rule' ? 'ルール検出' : '法令照合'}</Pill>
      </p>

      <p className="small" style={{ margin: '0 0 4px' }}>本文：「{finding.quote}」</p>

      {finding.guidance && (
        <p className="small" style={{ margin: '0 0 4px' }}>
          {finding.guidance}
        </p>
      )}

      {finding.citation ? (
        <p className="muted small" style={{ margin: 0 }}>
          出典：{finding.citation.publisher}／{finding.citation.title}
          {finding.citation.headingPath && ` / ${finding.citation.headingPath}`}
          <span style={{ display: 'block' }}>引用：「{finding.citation.excerpt}」</span>
          {finding.citation.canonicalUrl && (
            <a href={finding.citation.canonicalUrl} target="_blank" rel="noreferrer noopener">
              原本を開く
            </a>
          )}
        </p>
      ) : (
        <p className="muted small" style={{ margin: 0 }}>
          出典：本アプリの表現ルール（法令本文の引用はありません）
        </p>
      )}

      {error && (
        <p className="alert small" role="alert">
          {error}
        </p>
      )}

      {/*
        Three actions, and every one of them does something.

        Two are work - go to it, propose a replacement - and the third is the
        decision. The panel used to offer three DECISIONS and no work at all,
        with 該当箇所へ移動 tucked into the quotation above as a text link, which
        put the two useful controls in the least prominent place on the card.
      */}
      <div className="editpanel__actions">
        <button
          className="btn btn--ghost btn--sm"
          type="button"
          onClick={() => onJump(finding.at, finding.quote)}
        >
          該当箇所へ移動
        </button>
        <button
          className="btn btn--sm"
          type="button"
          disabled={fixing}
          onClick={() =>
            onFix({
              quote: finding.quote,
              title: finding.title,
              guidance: finding.guidance,
              citation: finding.citation,
            })
          }
        >
          {fixing ? '書き直し中…' : 'AIで直す'}
        </button>
        {decided ? (
          <>
            <Pill tone="muted">
              {DISPOSITION_LABEL[finding.disposition!.state] ?? finding.disposition!.state}
            </Pill>
            <span className="muted small">{jaDate(finding.disposition!.decided_at)}</span>
            {finding.disposition!.reason && (
              <span className="muted small">／{finding.disposition!.reason}</span>
            )}
            <button
              className="btn btn--ghost btn--sm"
              type="button"
              disabled={busy}
              onClick={() => void decide('OPEN')}
            >
              戻す
            </button>
          </>
        ) : (
          <button
            className="btn btn--ghost btn--sm"
            type="button"
            disabled={busy}
            onClick={() => void decide('DISMISSED')}
          >
            {busy ? '記録中…' : '無視する'}
          </button>
        )}
      </div>
    </div>
  );
}

export function LegalReview({
  articleId,
  state,
  onJump,
  onFix,
  busy,
  onChanged,
}: {
  articleId: string;
  state: ArticleReview;
  onJump: (at: number, quote: string) => void;
  /** Propose a replacement for a finding's sentence. See Finding, above. */
  onFix: (input: {
    quote: string;
    title: string;
    guidance?: string;
    citation?: { publisher: string; title: string; excerpt: string } | null;
  }) => void;
  /** A rewrite is already running; a second would race the first. */
  busy: boolean;
  onChanged: () => Promise<void>;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function run() {
    setRunning(true);
    setError(null);
    try {
      await post(`/articles/${articleId}/review`, {});
      await onChanged();
    } catch (err) {
      setError(err instanceof ApiError ? err.message : 'チェックできませんでした');
    } finally {
      setRunning(false);
    }
  }

  const review = state.review;
  const open = (review?.findings ?? []).filter(
    (finding) => !finding.disposition || finding.disposition.state === 'OPEN',
  );

  return (
    <Card
      title={`法令チェック${review ? `（${review.verdict}）` : ''}`}
      caption={
        state.corpus
          ? `法令コーパス v${state.corpus.version_no} と本アプリの表現ルールで確認します。AI利用枠を消費します。`
          : '有効な法令コーパスがないため、本アプリの表現ルールのみで確認します。'
      }
      actions={
        <button className="btn btn--sm" type="button" disabled={running} onClick={() => void run()}>
          {running ? '確認中…' : review ? '再実行' : 'チェックを実行'}
        </button>
      }
    >
      {error && (
        <p className="alert" role="alert">
          {error}
        </p>
      )}

      {/*
        Said out loud, not left in a caption.

        With no active corpus release the check still runs, still reports a
        verdict, and still says 指摘なし - from the expression rules alone,
        having consulted no law at all. That reads exactly like a clean review,
        which is the one way this product can mislead somebody.
      */}
      {state.corpus === null && (
        <p className="alert alert--warn" role="status">
          有効な法令コーパスがないため、<strong>法令・ガイドラインとの照合は行われていません</strong>
          。この結果は本アプリの表現ルールのみによるものです。
        </p>
      )}

      {review === null ? (
        <p className="muted small" style={{ marginTop: 0 }}>
          指摘は「指摘なし」「要確認」「根拠不足」で表示され、法令に基づく指摘には出典と該当条文を
          併記します。<strong>「適合」とは表示しません。</strong>
        </p>
      ) : (
        <>
          {state.stale && (
            <p className="alert" role="status">
              {state.staleReason === 'article'
                ? '本文が変更されています。この結果は保存時点の本文に対するものです。'
                : '法令コーパスが更新されています。この結果は以前のコーパスに対するものです。'}
              再実行してください。
            </p>
          )}

          <p className="muted small" style={{ marginTop: 0 }}>
            第{review.article_version}版・{jaDate(review.checked_at)}実行・指摘{' '}
            {review.findings.length} 件（未対応 {open.length} 件）
            {review.dropped_citations > 0 &&
              `／出典を確認できなかった指摘 ${review.dropped_citations} 件は除外しました`}
          </p>

          {review.findings.length === 0 ? (
            <Empty>
              このルールと法令コーパスでは指摘がありません（適合の判断ではありません）。
            </Empty>
          ) : (
            <div style={{ display: 'grid', gap: 10 }}>
              {review.findings.map((finding) => (
                <Finding
                  key={finding.fingerprint}
                  finding={finding}
                  articleId={articleId}
                  onJump={onJump}
                  onFix={onFix}
                  busy={busy}
                  onChanged={onChanged}
                />
              ))}
            </div>
          )}
        </>
      )}
    </Card>
  );
}
